import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Fade,
  Grid,
  IconButton,
  Modal,
  Paper,
  Typography,
  Snackbar,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";

const ImagePreviewModal = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const CloseButton = styled(IconButton)({
  position: "absolute",
  right: 16,
  top: 16,
  color: "white",
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
});

const ParsedDataContainer = styled(Box)({
  backgroundColor: "rgba(28, 28, 30, 0.95)",
  padding: "20px",
  borderRadius: "8px",
  maxHeight: "80vh",
  overflowY: "auto",
  color: "white",
});

const TextActivityDashboard = ({ isTracked = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterToUserId = searchParams.get("filter_to_user_id");

  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prevId, setPrevId] = useState(null);
  const [nextId, setNextId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [currentTab, setCurrentTab] = useState(0);

  const fetchActivity = async (activityId) => {
    setLoading(true);
    try {
      const endpoint = isTracked
        ? "/api/admin/tracked_activity/"
        : "/api/admin/text_activity/";

      const url = new URL(endpoint, window.location.origin);
      if (activityId) {
        url.searchParams.append("id", activityId);
      }
      if (filterToUserId) {
        url.searchParams.append("filter_to_user_id", filterToUserId);
      }

      const response = await axios.get(url.toString());
      const data = response.data;
      setActivityData(data);
      setPrevId(data.prev_id);
      setNextId(data.next_id);
    } catch (error) {
      console.error("Error fetching activity:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchActivity(searchParams.get("id"));
  }, [searchParams]);

  const navigateActivity = (newId) => {
    if (!newId) return;

    // Update the URL to include the new ID
    const params = new URLSearchParams(searchParams);
    params.set("id", newId);

    // Preserve existing parameters
    if (filterToUserId) {
      params.set("filter_to_user_id", filterToUserId);
    }
    if (searchParams.get("tab")) {
      params.set("tab", searchParams.get("tab"));
    }

    // Navigate to the new activity
    navigate({
      pathname: "/admin",
      search: params.toString(),
    });
  };

  const handleImageClick = (imageUrl) => {
    setPreviewImage(imageUrl);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
    setPreviewOpen(false);
  };

  const renderTranscript = (transcript) => {
    return transcript.map((entry, index) => (
      <Box key={index} sx={{ mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: entry.name.toLowerCase() === "me" ? "#FF3B30" : "#4CAF50",
            fontWeight: "bold",
          }}
        >
          {entry.name}:
        </Typography>
        {entry.messages.map((msg, msgIndex) => (
          <Typography
            key={msgIndex}
            variant="body2"
            sx={{
              color: "rgba(255, 255, 255, 0.87)",
              ml: 2,
            }}
          >
            {msg}
          </Typography>
        ))}
      </Box>
    ));
  };

  const handleTrackActivity = async () => {
    if (isTracked) return;

    try {
      await axios.post(
        `/api/admin/track_activity/${activityData.activity_id}/`
      );
      setSnackbar({
        open: true,
        message: "Activity tracked successfully",
        severity: "success",
      });
    } catch (error) {
      const message = error.response?.data?.error || "Error tracking activity";
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (loading || !activityData) {
    return (
      <Box p={4}>
        <Typography variant="h5">Loading...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid container style={{ height: "calc(100vh - 100px)" }}>
        {/* Left Column - Parsed Data */}
        <Grid item xs={12} md={6}>
          <Paper
            style={{
              height: "100%",
              padding: "20px",
              overflowY: "auto",
              backgroundColor: "#1e1e1e",
              color: "#fff",
            }}
            elevation={3}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="h6">
                  {isTracked ? "Tracked Activity" : "Parsed Data"}
                </Typography>
                {!isTracked && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<TrackChangesIcon />}
                    onClick={handleTrackActivity}
                    size="small"
                  >
                    Track Activity
                  </Button>
                )}
              </Box>
              <Box>
                <Button
                  variant="contained"
                  onClick={() => navigateActivity(prevId)}
                  disabled={!prevId}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigateActivity(nextId)}
                  disabled={!nextId}
                  size="small"
                >
                  Next
                </Button>
              </Box>
            </Box>

            <ParsedDataContainer>
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => setCurrentTab(newValue)}
                sx={{
                  mb: 2,
                  borderBottom: 1,
                  borderColor: "divider",
                  "& .MuiTab-root": { color: "rgba(255, 255, 255, 0.7)" },
                  "& .Mui-selected": { color: "white" },
                }}
              >
                <Tab label="Parsed Data" />
                {activityData.messages?.length > 0 && (
                  <Tab label="User Follow Up Messages" />
                )}
              </Tabs>

              {currentTab === 0 ? (
                <>
                  {/* Background */}
                  <Typography variant="h6" sx={{ mb: 2, color: "#FF3B30" }}>
                    Background
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 3, whiteSpace: "pre-wrap" }}
                  >
                    {activityData.parsed_data_from_images.background}
                  </Typography>

                  {/* Profile Details */}
                  <Typography variant="h6" sx={{ mb: 2, color: "#FF3B30" }}>
                    Profile Details
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 3, whiteSpace: "pre-wrap" }}
                  >
                    {
                      activityData.parsed_data_from_images
                        .dating_profile_details
                    }
                  </Typography>

                  {/* Other Person */}
                  <Typography variant="h6" sx={{ mb: 2, color: "#FF3B30" }}>
                    Other Person
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 3, whiteSpace: "pre-wrap" }}
                  >
                    {activityData.parsed_data_from_images.other_person_name}
                  </Typography>

                  {/* Transcript */}
                  {activityData.parsed_data_from_images.transcript?.length >
                    0 && (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, color: "#FF3B30" }}>
                        Transcript
                      </Typography>
                      {renderTranscript(
                        activityData.parsed_data_from_images.transcript
                      )}
                    </>
                  )}

                  {/* Observations */}
                  {activityData.parsed_data_from_images.observations?.length >
                    0 && (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, color: "#FF3B30" }}>
                        Observations
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        {activityData.parsed_data_from_images.observations.map(
                          (obs, index) => (
                            <Typography
                              key={index}
                              variant="body2"
                              sx={{ mb: 1, whiteSpace: "pre-wrap" }}
                            >
                              • {obs}
                            </Typography>
                          )
                        )}
                      </Box>
                    </>
                  )}
                </>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {activityData.messages?.map((message, index) => {
                    if (message.role === "system") return null;
                    const isUser = message.role === "user";

                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255, 255, 255, 0.6)",
                            mb: 0.5,
                            alignSelf: isUser ? "flex-end" : "flex-start",
                          }}
                        >
                          {isUser ? "User" : "Assistant"}
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: isUser ? "#0084ff" : "#3a3b3c",
                            color: "white",
                            padding: "8px 12px",
                            borderRadius: "18px",
                            maxWidth: "80%",
                            marginBottom: "8px",
                            alignSelf: isUser ? "flex-end" : "flex-start",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {message.content}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </ParsedDataContainer>
          </Paper>
        </Grid>

        {/* Right Column - Images */}
        <Grid item xs={12} md={6}>
          <Paper
            style={{
              height: "100%",
              padding: "20px",
              overflowY: "auto",
              backgroundColor: "#1e1e1e",
              color: "#fff",
            }}
            elevation={3}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" color="inherit">
                Activity {activityData.index + 1} of {activityData.total}
              </Typography>
            </Box>

            {activityData.images.length === 0 ? (
              <Typography color="inherit">No images available.</Typography>
            ) : activityData.images.length === 1 ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "20px",
                }}
              >
                <img
                  src={activityData.images[0]}
                  alt="Activity"
                  onClick={() => handleImageClick(activityData.images[0])}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "calc(100vh - 180px)",
                    objectFit: "contain",
                    cursor: "pointer",
                  }}
                />
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ marginBottom: "20px" }}>
                {activityData.images.map((imgUrl, idx) => (
                  <Grid item xs={6} key={idx}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={imgUrl}
                        alt={`Activity ${idx + 1}`}
                        onClick={() => handleImageClick(imgUrl)}
                        style={{
                          width: "100%",
                          maxHeight: "calc((100vh - 180px) / 2)",
                          objectFit: "contain",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Add Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ImagePreviewModal
        open={previewOpen}
        onClose={handleClosePreview}
        closeAfterTransition
      >
        <Fade in={previewOpen}>
          <Box
            sx={{
              outline: "none",
              position: "relative",
            }}
          >
            <CloseButton onClick={handleClosePreview}>
              <CloseIcon />
            </CloseButton>
            {previewImage && (
              <img
                src={previewImage}
                alt="Preview"
                style={{
                  maxWidth: "90vw",
                  maxHeight: "80vh",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>
        </Fade>
      </ImagePreviewModal>
    </>
  );
};

export default TextActivityDashboard;
